import _ from "underscore";
import { hideLoader, hideUnobtrusiveLoader, showLoader, showUnobtrusiveLoader } from "../plugins";
import { isNotEmpty, isEmpty } from "../framework/utils";
import * as preferences from "../framework/preferences";

const CAMERA_ID_KEY = "camera.lastDeviceId";
const ASPECT_RATIO = 4 / 3;

let _cameraAvailable;
let _stream;

function enforceAspectRatio(width, height, aspectRatio) {

    if (isEmpty(width) || isEmpty(height) || isEmpty(aspectRatio)) {
        return {}
    }

    const actualAspectRatio = width / height,
        resolution = {};

    if (actualAspectRatio > aspectRatio) {
        resolution.height = height;
        resolution.width = height * aspectRatio;
    } else {
        resolution.width = width;
        resolution.height = width / aspectRatio;
    }

    return resolution;
}

export function checkCameraAvailability() {
    return new Promise((resolve, reject) => {
        function handleResponse() {
            if (_cameraAvailable) {
                resolve();
            } else {
                reject();
            }
        }

        if (_cameraAvailable === undefined) {
            showUnobtrusiveLoader();
            navigator.mediaDevices.getUserMedia({video: { facingMode: { ideal: 'environment' } } })
                .then(stream => {
                    stream.getTracks()
                        .forEach(track => track.stop());
                    _cameraAvailable = true;
                })
                .catch(e => {
                    console.log(e);
                    _cameraAvailable = false;
                })
                .finally(() => {
                    hideUnobtrusiveLoader();
                    handleResponse();
                })
        } else {
            handleResponse();
        }
    })
}

function getLastDeviceId() {
    return new Promise((resolve, reject) => {
        preferences.load()
            .then(() => preferences.get(CAMERA_ID_KEY))
            .catch(e => {
                console.log(e);
            })
            .finally(resolve); //we only want soft fail for this
    })
}

function setLastDeviceId(deviceId) {
    return new Promise((resolve, reject) => {
        preferences.load()
            .then(() => {
                return isNotEmpty(deviceId)
                    ? preferences.set(CAMERA_ID_KEY, deviceId)
                    : preferences.clear(CAMERA_ID_KEY);
            })
            .then(() => preferences.save())
            .catch(e => {
                console.log(e);
            })
            .finally(resolve); //we only want soft fail for this
    })
}

export function getVideoDevices() {
    return new Promise((resolve, reject) => {
        const devicesData = {};
        
        showUnobtrusiveLoader();
        getLastDeviceId()
            .then((deviceId) => {
                devicesData.deviceId = deviceId;
                return checkCameraAvailability();
            })
            .then(() => navigator.mediaDevices.enumerateDevices())
            .then(devices => {
                const videoDevices = _.where(devices, {kind: "videoinput"});
                devicesData.devices = videoDevices;
                resolve(devicesData);
            })
            .catch(reject)
            .finally(hideUnobtrusiveLoader);
    })
}

export function startStream(deviceId, options = {}) {
    if (isNotEmpty(_stream)) {
        stopStream();
    }

    return new Promise((resolve, reject) => {
        const mediaOptions = isNotEmpty(deviceId)
            ? {
                video: {
                    deviceId: deviceId,
                }
            }
            : {
                video: { facingMode: { ideal: 'environment' } } 
            };

            showLoader();
            setLastDeviceId(deviceId)
                .then(() => navigator.mediaDevices.getUserMedia(mediaOptions))
                .then(stream => {
                    const maxRes = options?.maxRes ?? false,
                        aspectRatio = options?.aspectRatio ?? ASPECT_RATIO;

                    if (maxRes) {
                        const track = stream.getVideoTracks()[0],
                            {height: heightRange, width: widthRange} = track.getCapabilities(),
                            maxHeigth = heightRange?.max ?? undefined,
                            maxWidth = widthRange?.max ?? undefined;
                            
                        if (maxWidth !== undefined && maxHeigth !== undefined) {
                            const {width, height} = enforceAspectRatio(maxWidth, maxHeigth, aspectRatio);
                            return track
                                .applyConstraints({width: width, height: height})
                                .then(() => Promise.resolve(stream));
                        } else {
                            return Promise.resolve(stream);
                        }
                    } else {
                        return Promise.resolve(stream);
                    }
                })
                .then(stream => {
                    _stream = stream;
                    resolve(_stream);
                })
                .catch(reject)
                .finally(hideLoader);
    })
}

export function stopStream() {
    if (isNotEmpty(_stream)) {
        _stream.getVideoTracks().forEach(track => track.stop());
        _stream = undefined;
    }
}

export function takePhoto(options = {}) {
    
    return new Promise((resolve, reject) => {
        const initPromise = isNotEmpty(_stream)
            ? () => new Promise((resolve, reject) => resolve())
            : startStream;

        showLoader();
        initPromise()
            .then(() => {
                const track = _stream.getVideoTracks()[0],
                    imageCapture = new ImageCapture(track);

                return imageCapture.getPhotoCapabilities()
                    .then(capabilities => {
                        return Promise.resolve({
                            imageCapture: imageCapture,
                            capabilities: capabilities,
                            track: track,
                        })
                    })
            })
            .then(data => {
                const {imageCapture, capabilities} = data,
                    imageHeight = capabilities?.imageHeight?.max ?? undefined,
                    imageWidth = capabilities?.imageWidth?.max ?? undefined,
                    settings = {};

                if (imageWidth !== undefined && imageHeight !== undefined) {
                    settings.imageWidth = imageWidth;
                    settings.imageHeight = imageHeight;
                }

                return imageCapture.takePhoto(settings)
                    .then(blob => {
                        data.blob = blob;
                        return Promise.resolve(data);
                    })
            })
            .then(data => {
                const imageCapture = data.imageCapture;
                return imageCapture.getPhotoSettings()
                    .then(settings => {
                        data.settings = settings;
                        return Promise.resolve(data);
                    })
            })
            .then(data => {
                const {settings, track, blob} = data,
                    maxRes = options?.maxRes ?? true,
                    {imageWidth, imageHeight} = settings,
                    aspectRatio = track.getSettings()?.aspectRatio ?? undefined;

                if (aspectRatio !== undefined
                    && imageHeight !== undefined
                    && imageWidth !== undefined
                    && !maxRes) {
                    return new Promise((resolve, reject) => {
                        const {width, height} = enforceAspectRatio(imageWidth, imageHeight, aspectRatio),
                            canvas = document.createElement("canvas"),
                            context = canvas.getContext("2d"),
                            img = document.createElement("img"),
                            url = URL.createObjectURL(blob);

                        canvas.width = width;
                        canvas.height = height;

                        img.onload = () => {
                            const sx = (imageWidth - width) / 2,
                                sy = (imageHeight - height) / 2;

                            context.resetTransform();

                            if (options?.zoom) {    
                                const zoom = options.zoom;                            
                                context.translate((width * (zoom - 1)) / -2, (height * (zoom - 1)) / -2);
                                context.scale(zoom, zoom);
                            }

                            context.drawImage(
                                img,
                                sx,
                                sy,
                                width,
                                height,
                                0,
                                0,
                                width,
                                height
                            );
                            try {
                                canvas.toBlob((blob) => {
                                    URL.revokeObjectURL(url);
                                    canvas.remove();
                                    img.remove();
                                    resolve(blob);
                                })
                            } catch (e) {
                                reject(e);
                            }
                        };
                        img.onerror = reject;
                        img.src = url;
                    })
                } else {
                    return Promise.resolve(blob);
                }
            })
            .then(resolve)
            .catch(reject)
            .finally(hideLoader);
    })
}